import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
/**
 * Flatten a plugin's tagged capabilities into the flat shape consumers read,
 * rejecting empty arms. A declared capability arm reads as intent: announcing a
 * `kind` but contributing nothing does nothing, so reject a declared
 * capability arm that carries no entries (absent kinds stay
 * valid). A plugin whose declared arms leave it with no components, providers,
 * or plus-menu items is likewise rejected.
 */
export function collectCapabilities(id, capabilities) {
    const components = [];
    const providers = [];
    const plusMenuItems = [];
    const plusMenuOpenChange = [];
    const primaryActions = [];
    let filePicker;
    let fileDrop;
    let filePaste;
    let keyboard;
    let link;
    const mentions = [];
    for (const capability of capabilities) {
        switch (capability.kind) {
            case 'slot':
                if (capability.components.length === 0) {
                    throw new Error(`createPlugin(${id}): a 'slot' capability must place at least one component.`);
                }
                components.push(...capability.components);
                break;
            case 'typeahead':
                if (capability.providers.length === 0) {
                    throw new Error(`createPlugin(${id}): a 'typeahead' capability must register at least one provider.`);
                }
                providers.push(...capability.providers);
                break;
            case 'plusMenu':
                if (typeof capability.items !== 'function') {
                    throw new Error(`createPlugin(${id}): a 'plusMenu' capability must provide an item loader.`);
                }
                plusMenuItems.push(wrapPlusMenuItemLoader(id, capability.items));
                if (capability.onOpenChange !== undefined) {
                    plusMenuOpenChange.push(capability.onOpenChange);
                }
                break;
            case 'primaryAction':
                // The type interface requires these fields for TS callers; keep the
                // runtime guard consistent with the other capability arms for JS/dynamic
                // callers that can bypass those types.
                if (!capability.id ||
                    typeof capability.when !== 'function' ||
                    typeof capability.action !== 'function') {
                    throw new Error(`createPlugin(${id}): a 'primaryAction' capability must provide an id, a when predicate, and an action.`);
                }
                primaryActions.push({
                    id: capability.id,
                    priority: capability.priority,
                    when: capability.when,
                    action: capability.action,
                });
                break;
            case 'filePicker':
                if (typeof capability.configure !== 'function') {
                    throw new Error(`createPlugin(${id}): a 'filePicker' capability must provide a configure function.`);
                }
                if (filePicker !== undefined) {
                    throw new Error(`createPlugin(${id}): a plugin may declare only one 'filePicker' capability.`);
                }
                filePicker = capability.configure;
                break;
            case 'fileDrop':
                if (typeof capability.configure !== 'function' ||
                    typeof capability.renderOverlay !== 'function') {
                    throw new Error(`createPlugin(${id}): a 'fileDrop' capability must provide configure and renderOverlay functions.`);
                }
                if (fileDrop !== undefined) {
                    throw new Error(`createPlugin(${id}): a plugin may declare only one 'fileDrop' capability.`);
                }
                fileDrop = Object.freeze({
                    configure: capability.configure,
                    renderOverlay: capability.renderOverlay,
                });
                break;
            case 'filePaste':
                if (typeof capability.configure !== 'function') {
                    throw new Error(`createPlugin(${id}): a 'filePaste' capability must provide a configure function.`);
                }
                if (filePaste !== undefined) {
                    throw new Error(`createPlugin(${id}): a plugin may declare only one 'filePaste' capability.`);
                }
                filePaste = capability.configure;
                break;
            case 'keyboard':
                if (typeof capability.configure !== 'function') {
                    throw new Error(`createPlugin(${id}): a 'keyboard' capability must provide a configure function.`);
                }
                if (keyboard !== undefined) {
                    throw new Error(`createPlugin(${id}): a plugin may declare only one 'keyboard' capability.`);
                }
                keyboard = capability.configure;
                break;
            case 'link':
                if (typeof capability.configure !== 'function') {
                    throw new Error(`createPlugin(${id}): a 'link' capability must provide a configure function.`);
                }
                if (link !== undefined) {
                    throw new Error(`createPlugin(${id}): a plugin may declare only one 'link' capability.`);
                }
                link = capability.configure;
                break;
            case 'mention':
                if (typeof capability.configure !== 'function') {
                    throw new Error(`createPlugin(${id}): a 'mention' capability must provide a configure function.`);
                }
                mentions.push(capability.configure);
                break;
            default:
                assertUnreachableValue(capability);
        }
    }
    if (components.length === 0 &&
        providers.length === 0 &&
        plusMenuItems.length === 0 &&
        primaryActions.length === 0 &&
        filePicker === undefined &&
        fileDrop === undefined &&
        filePaste === undefined &&
        keyboard === undefined &&
        link === undefined &&
        mentions.length === 0) {
        throw new Error(`createPlugin(${id}): a plugin must place at least one component, typeahead provider, plus-menu item, primary action, file picker, file drop zone, file paste handler, keyboard handler, link handler, or mention handler.`);
    }
    return {
        components,
        providers,
        plusMenuItems,
        plusMenuOpenChange,
        primaryActions,
        filePicker,
        fileDrop,
        filePaste,
        keyboard,
        link,
        mentions,
    };
}
function wrapPlusMenuItemLoader(id, loadItems) {
    return async () => {
        const items = await loadItems();
        if (items.length === 0) {
            throw new Error(`createPlugin(${id}): a 'plusMenu' capability must resolve at least one item.`);
        }
        return items;
    };
}
//# sourceMappingURL=utils.js.map